import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DoCheck, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { LocalizationModule } from '@nimbus-library/features/localization/localization.module';
import { PluralForm } from '@nimbus-library/features/localization/models/enums/pluralform.enum';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { IOptionItem } from '@nimbus-library/features/shared/models/interfaces/i-option-item';
import { SharedUIModule } from '@nimbus-library/features/shared/shared.ui.module';
import { GroupSelectionLookupService } from '../../services/group-selection-lookup.service';

@Component({
  selector: 'nimbus-group-selection-form-control',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SharedUIModule, LocalizationModule],
  templateUrl: './group-selection-form-control.component.html',
  styleUrls: ['./group-selection-form-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GroupSelectionFormControlComponent implements OnInit, DoCheck {
  @Input({ required: true })
  public control: FormControl<string>;
  @Input()
  public required: boolean = false;
  @Input()
  public contextChanged: (context: Record<string, string>) => void;

  public readonly TranslationKeys = TranslationKeys;
  public readonly PluralForm = PluralForm;

  public isPartnerRequired: boolean = false;
  public form: FormGroup<{ partnerId: FormControl<string> }>;

  public partners$: Observable<IOptionItem[]>;
  public tenants$: Observable<IOptionItem[]>;

  public constructor(private readonly groupSelectionLookupService: GroupSelectionLookupService) {}

  public ngOnInit(): void {
    this.form = new FormGroup({
      partnerId: new FormControl('', { nonNullable: true }),
    });
    this.isPartnerRequired = this.groupSelectionLookupService.isPartnerRequired;
    this.syncFormDisabled();

    if (!this.isPartnerRequired) {
      this.partners$ = of([]);
      this.tenants$ = this.loadTenants('', true);
      return;
    }

    this.initializePartnerSelection();
  }

  public ngDoCheck(): void {
    this.syncFormDisabled();
  }

  private initializePartnerSelection(): void {
    const initialPartner$ = this.control.value ? this.groupSelectionLookupService.getTenantPartnerId(this.control.value) : of('');

    const partnerSelection$ = forkJoin({ partners: this.groupSelectionLookupService.getPartners(), partnerId: initialPartner$ }).pipe(
      map(({ partners, partnerId }) => {
        const isPartnerAvailable = !partnerId || partners.some((partner) => partner.id === partnerId);
        if (!isPartnerAvailable) {
          this.clearContext();
        }
        return { partners, partnerId: isPartnerAvailable ? partnerId : '' };
      }),
      tap(({ partnerId }) => {
        this.form.controls.partnerId.setValue(partnerId, { emitEvent: false });
        this.syncFormDisabled();
      }),
      tap({
        error: (error: unknown) => {
          console.error(error);
          this.clearContext();
        },
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.partners$ = partnerSelection$.pipe(map(({ partners }) => partners));
    this.tenants$ = partnerSelection$.pipe(
      switchMap(({ partnerId }) =>
        this.form.controls.partnerId.valueChanges.pipe(
          startWith(partnerId),
          distinctUntilChanged(),
          switchMap((id, index) => this.loadTenants(id, index === 0)),
        ),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private loadTenants(partnerId: string, isInitialLoad: boolean): Observable<IOptionItem[]> {
    this.contextChanged?.(partnerId ? { partnerId } : {});
    if (!isInitialLoad) {
      this.control.markAsDirty();
    }
    if (!isInitialLoad || (this.isPartnerRequired && !partnerId)) {
      this.clearTenant();
    }
    return this.groupSelectionLookupService.getTenants(partnerId).pipe(
      catchError((error: unknown) => {
        console.error(error);
        this.clearTenant();
        return of([]);
      }),
      tap((tenants) => {
        if (this.control.value && !tenants.some((tenant) => tenant.id === this.control.value)) {
          this.clearTenant();
        }
      }),
      startWith([]),
    );
  }

  private syncFormDisabled(): void {
    const disabled = this.control.disabled;
    if (disabled === this.form.disabled) {
      return;
    }
    if (disabled) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  }

  private clearTenant(): void {
    if (this.control.value) {
      this.control.setValue('');
    }
  }

  private clearContext(): void {
    this.contextChanged?.({});
    this.clearTenant();
  }
}
